import DirectionsCarFilledIcon from "@mui/icons-material/DirectionsCarFilled";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import React from "react";

import { ROLE_API_ADMIN_APPROVE } from "../constants/appRoles";
import { useVehicleSoftwareEventScanApi } from "../hooks/useVehicleSoftwareEventScanApi";
import type {
  VehicleSoftwareEventScanReportDto,
  VehicleSoftwareEventScanRowDto,
  VehicleSoftwareScanBasis,
  VehicleSoftwareScanCoverageGapRow,
} from "../shared/vehicleSoftwareEventScan";
import { formatLocalDateTime } from "../utils/formatLocalDateTime";
import AdminRoleInfoTooltip from "./AdminRoleInfoTooltip";

type Props = {
  active: boolean;
  /** Opens the event edit dialog on the Admin page (same as other admin tools). */
  onOpenEvent?: (globalEventId: number) => void;
};

const ROW_PAGE = 40;
const GAP_PAGE = 50;

/** Tab 0 = latest per software; tab 1 = union of all uploads. */
const AdminVehicleSoftwareScanPanel = ({ active, onOpenEvent }: Props) => {
  const { listReports, listRows, listCoverageGaps } = useVehicleSoftwareEventScanApi();
  const [softwareFilter, setSoftwareFilter] = React.useState("");
  const [listLoading, setListLoading] = React.useState(false);
  const [listError, setListError] = React.useState<string | null>(null);
  const [reports, setReports] = React.useState<VehicleSoftwareEventScanReportDto[]>([]);
  const [listTotal, setListTotal] = React.useState(0);
  const [listOffset, setListOffset] = React.useState(0);
  const listLimit = 25;

  const [archiveExpanded, setArchiveExpanded] = React.useState(false);

  const [expandedId, setExpandedId] = React.useState<number | null>(null);
  const [rows, setRows] = React.useState<VehicleSoftwareEventScanRowDto[]>([]);
  const [rowsLoading, setRowsLoading] = React.useState(false);
  const [rowsError, setRowsError] = React.useState<string | null>(null);
  const [rowsTotal, setRowsTotal] = React.useState(0);
  const [rowsOffset, setRowsOffset] = React.useState(0);

  const [gapTab, setGapTab] = React.useState(0);
  const gapsScanBasis: VehicleSoftwareScanBasis =
    gapTab === 0 ? "latest_per_software" : "all_reports";

  const [gapsLegacy, setGapsLegacy] = React.useState(false);
  /** When true, omit resolving comparison codes [10000, 20000) from gap analysis */
  const [gapsExcludeResolving, setGapsExcludeResolving] = React.useState(false);
  const [gapsLoading, setGapsLoading] = React.useState(false);
  const [gapsError, setGapsError] = React.useState<string | null>(null);
  const [gapsRows, setGapsRows] = React.useState<VehicleSoftwareScanCoverageGapRow[]>([]);
  const [gapsTotal, setGapsTotal] = React.useState(0);
  const [gapsOffset, setGapsOffset] = React.useState(0);
  const loadList = React.useCallback(
    async (off: number) => {
      setListLoading(true);
      setListError(null);
      try {
        const res = await listReports({
          offset: off,
          limit: listLimit,
          softwareName: softwareFilter.trim() || undefined,
        });
        setListOffset(off);
        setReports(res.results ?? []);
        setListTotal(res.total ?? 0);
      } catch (e: unknown) {
        const status = (e as { status?: number })?.status;
        setListError(
          status === 403
            ? `Forbidden — assign API role ${ROLE_API_ADMIN_APPROVE} and sign in again.`
            : "Failed to load vehicle software event scans."
        );
        setReports([]);
        setListTotal(0);
      } finally {
        setListLoading(false);
      }
    },
    [listReports, listLimit, softwareFilter]
  );

  const loadGaps = React.useCallback(
    async (off: number) => {
      setGapsLoading(true);
      setGapsError(null);
      try {
        const res = await listCoverageGaps({
          offset: off,
          limit: GAP_PAGE,
          useLegacyEventCode: gapsLegacy,
          scanBasis: gapsScanBasis,
          excludeResolvingEvents: gapsExcludeResolving || undefined,
        });
        setGapsOffset(off);
        setGapsRows(res.results ?? []);
        setGapsTotal(res.total ?? 0);
      } catch (e: unknown) {
        const st = (e as { status?: number })?.status;
        setGapsError(
          st === 403
            ? `Forbidden — assign API role ${ROLE_API_ADMIN_APPROVE} and sign in again.`
            : "Failed to load coverage gaps."
        );
        setGapsRows([]);
        setGapsTotal(0);
      } finally {
        setGapsLoading(false);
      }
    },
    [listCoverageGaps, gapsLegacy, gapsScanBasis, gapsExcludeResolving]
  );

  const loadRows = React.useCallback(
    async (reportId: number, offset: number) => {
      setRowsLoading(true);
      setRowsError(null);
      try {
        const res = await listRows(reportId, { offset, limit: ROW_PAGE });
        setRows(res.results ?? []);
        setRowsTotal(res.total ?? 0);
        setRowsOffset(offset);
      } catch {
        setRowsError("Failed to load scan rows.");
        setRows([]);
        setRowsTotal(0);
      } finally {
        setRowsLoading(false);
      }
    },
    [listRows]
  );

  React.useEffect(() => {
    if (!active || !archiveExpanded) return;
    void loadList(0);
    // Only when the archive opens — loadList changes with the software filter text.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, archiveExpanded]);

  React.useEffect(() => {
    if (!active) return;
    setGapsOffset(0);
    void loadGaps(0);
  }, [active, gapsScanBasis, gapsLegacy, gapsExcludeResolving, loadGaps]);

  const toggleExpand = (id: number) => {
    if (expandedId === id) {
      setExpandedId(null);
      setRows([]);
      setRowsOffset(0);
      setRowsTotal(0);
      return;
    }
    setExpandedId(id);
    setRowsOffset(0);
    void loadRows(id, 0);
  };

  const onArchiveAccordionChange = (_: React.SyntheticEvent, expanded: boolean) => {
    setArchiveExpanded(expanded);
    if (!expanded) {
      setExpandedId(null);
      setRows([]);
      setRowsError(null);
      setRowsTotal(0);
      setRowsOffset(0);
    }
  };

  if (!active) {
    return null;
  }

  return (
    <Box sx={{ px: 2, pb: 2 }}>
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
        <Typography variant="subtitle1" fontWeight={600} component="span">
          Catalog vs. vehicle software scans
        </Typography>
        <AdminRoleInfoTooltip ariaLabel="Vehicle software scan API permissions">
          <>
            Same API role as other admin readouts:{" "}
            <strong>
              <code>{ROLE_API_ADMIN_APPROVE}</code>
            </strong>{" "}
            on the Event Manager API app in Entra.
          </>
        </AdminRoleInfoTooltip>
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 800 }}>
        Rows whose comparison code never appears as <code>EventCode</code> in pipeline CSVs. The catalog side is
        always the latest row per code, type, and contract (non-superseded), excluding manufacturer events. Default{" "}
        <strong>UserEvent</strong> only (override via API <code>event_type</code> if added later).         Tabs control scan scope (default: newest report per software vs all historical uploads). Optional: exclude
        resolving events (codes 10k–20k) from the gap list for legacy comparisons.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Tabs
          value={gapTab}
          onChange={(_, v) => setGapTab(v)}
          variant="fullWidth"
          sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}
        >
          <Tab
            label="Missing from latest uploads"
            id="vehicle-scan-tab-latest"
            aria-controls="vehicle-scan-gap-panel"
            sx={{ textTransform: "none", fontWeight: 600 }}
          />
          <Tab
            label="Never in any scan"
            id="vehicle-scan-tab-never"
            aria-controls="vehicle-scan-gap-panel"
            sx={{ textTransform: "none", fontWeight: 600 }}
          />
        </Tabs>

        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
          {gapTab === 0
            ? "Only the newest report per software name — events not referenced in those current snapshots."
            : "Union across every stored CSV — codes never reported in any pipeline upload."}
        </Typography>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }} sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
          <FormControlLabel
            control={
              <Switch
                checked={gapsLegacy}
                onChange={(_, c) => setGapsLegacy(c)}
                size="small"
                disabled={gapsLoading}
              />
            }
            label="Use legacy code for comparison"
          />
          <FormControlLabel
            control={
              <Switch
                checked={gapsExcludeResolving}
                onChange={(_, c) => setGapsExcludeResolving(c)}
                size="small"
                disabled={gapsLoading}
              />
            }
            label="Exclude resolving events"
          />
          <Button variant="outlined" size="small" onClick={() => void loadGaps(gapsOffset)} disabled={gapsLoading}>
            {gapsLoading ? "Loading…" : "Refresh"}
          </Button>
        </Stack>

        {!gapsLoading && !gapsError && (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }} alignItems="center">
            <Chip
              size="small"
              color={gapsTotal > 0 ? "warning" : "default"}
              variant={gapsTotal > 0 ? "filled" : "outlined"}
              label={`${gapsTotal} catalog gap${gapsTotal === 1 ? "" : "s"}`}
            />
          </Stack>
        )}

        {gapsError && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {gapsError}
          </Alert>
        )}

        {gapsLoading && gapsRows.length === 0 ? (
          <Stack alignItems="center" py={2}>
            <CircularProgress size={28} />
          </Stack>
        ) : (
          <>
            <TableContainer sx={{ maxHeight: 420 }} id="vehicle-scan-gap-panel" role="tabpanel">
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Compare code</TableCell>
                    <TableCell>Event code</TableCell>
                    <TableCell>Legacy code</TableCell>
                    <TableCell>Event contract name</TableCell>
                    <TableCell>Label (eng)</TableCell>
                    {onOpenEvent ? <TableCell align="right"> </TableCell> : null}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {gapsRows.map((row) => (
                    <TableRow key={row.global_event_id} hover>
                      <TableCell>{row.comparison_code}</TableCell>
                      <TableCell>{row.event_code}</TableCell>
                      <TableCell>{row.event_code_old ?? "—"}</TableCell>
                      <TableCell sx={{ maxWidth: 220 }} title={row.event_contract_name}>
                        <Typography variant="body2" noWrap>
                          {row.event_contract_name}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 200 }} title={row.label_eng ?? ""}>
                        <Typography variant="body2" noWrap>
                          {row.label_eng ?? "—"}
                        </Typography>
                      </TableCell>
                      {onOpenEvent ? (
                        <TableCell align="right">
                          <Button size="small" variant="outlined" onClick={() => onOpenEvent(row.global_event_id)}>
                            Open event
                          </Button>
                        </TableCell>
                      ) : null}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {!gapsLoading && gapsRows.length === 0 && !gapsError && (
              <Typography variant="body2" color="success.main" sx={{ py: 1 }}>
                No gaps — every catalog comparison code appears in the selected scan scope.
              </Typography>
            )}
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {gapsTotal === 0 ? 0 : gapsOffset + 1}-{Math.min(gapsOffset + gapsRows.length, gapsTotal)} of {gapsTotal}
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={gapsOffset === 0 || gapsLoading}
                  onClick={() => void loadGaps(Math.max(0, gapsOffset - GAP_PAGE))}
                >
                  Previous
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={gapsOffset + GAP_PAGE >= gapsTotal || gapsLoading}
                  onClick={() => void loadGaps(gapsOffset + GAP_PAGE)}
                >
                  Next
                </Button>
              </Stack>
            </Stack>
          </>
        )}
      </Paper>

      <Accordion
        disableGutters
        expanded={archiveExpanded}
        onChange={onArchiveAccordionChange}
        sx={{ "&:before": { display: "none" }, border: 1, borderColor: "divider", borderRadius: 1 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="vehicle-scan-archive" id="vehicle-scan-archive-header">
          <Stack direction="row" alignItems="center" spacing={1} sx={{ pr: 1 }}>
            <DirectionsCarFilledIcon color="action" fontSize="small" />
            <Typography fontWeight={600}>Import archive</Typography>
            <Typography variant="caption" color="text.secondary">
              {archiveExpanded || listTotal > 0
                ? `${listTotal} CSV upload${listTotal === 1 ? "" : "s"} stored`
                : "Expand to browse pipeline uploads and raw rows"}
            </Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Raw pipeline CSV scans by software name (pagination, optional filter). Use this when you need to inspect a
            specific upload — not required for gap analysis above.
          </Typography>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mb: 1.5 }} alignItems={{ sm: "center" }}>
            <TextField
              size="small"
              label="Filter by software name"
              value={softwareFilter}
              onChange={(e) => setSoftwareFilter(e.target.value)}
              sx={{ minWidth: 220 }}
            />
            <Button variant="outlined" onClick={() => void loadList(0)} disabled={listLoading}>
              {listLoading ? "Loading…" : "Apply filter"}
            </Button>
            <Button variant="text" onClick={() => void loadList(listOffset)} disabled={listLoading}>
              Refresh list
            </Button>
          </Stack>

          {listError && (
            <Alert severity="error" sx={{ mb: 1 }}>
              {listError}
            </Alert>
          )}

          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Showing {listTotal === 0 ? 0 : listOffset + 1}-{Math.min(listOffset + reports.length, listTotal)} of{" "}
              {listTotal}
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant="outlined"
                disabled={listOffset === 0 || listLoading}
                onClick={() => void loadList(Math.max(0, listOffset - listLimit))}
              >
                Previous
              </Button>
              <Button
                size="small"
                variant="outlined"
                disabled={listOffset + listLimit >= listTotal || listLoading}
                onClick={() => void loadList(listOffset + listLimit)}
              >
                Next
              </Button>
            </Stack>
          </Stack>

          {listLoading && reports.length === 0 ? (
            <Stack alignItems="center" py={2}>
              <CircularProgress size={28} />
            </Stack>
          ) : (
            <Stack spacing={1}>
              {reports.map((r) => (
                <Paper key={r.id} variant="outlined" sx={{ p: 1.25 }}>
                  <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                    <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ minWidth: 0 }}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={600} noWrap title={r.software_name}>
                          {r.software_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {formatLocalDateTime(r.created_at)} · {r.row_count} rows
                          {r.caller_app_id ? ` · caller ${r.caller_app_id}` : ""}
                        </Typography>
                        {Object.keys(r.metadata ?? {}).length > 0 && (
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                            {JSON.stringify(r.metadata)}
                          </Typography>
                        )}
                      </Box>
                    </Stack>
                    <Button size="small" variant="outlined" onClick={() => toggleExpand(r.id)}>
                      {expandedId === r.id ? "Hide rows" : "View rows"}
                    </Button>
                  </Stack>

                  {expandedId === r.id && (
                    <Box sx={{ mt: 1.5 }}>
                      {rowsError && (
                        <Alert severity="error" sx={{ mb: 1 }}>
                          {rowsError}
                        </Alert>
                      )}
                      {rowsLoading && rows.length === 0 ? (
                        <CircularProgress size={24} />
                      ) : (
                        <>
                          <TableContainer sx={{ maxHeight: 360 }}>
                            <Table size="small" stickyHeader>
                              <TableHead>
                                <TableRow>
                                  <TableCell>Event code</TableCell>
                                  <TableCell>Filename</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {rows.map((row) => (
                                  <TableRow key={row.id}>
                                    <TableCell>{row.event_code ?? "—"}</TableCell>
                                    <TableCell sx={{ maxWidth: 360 }} title={row.source_filename ?? undefined}>
                                      <Typography variant="body2" noWrap>
                                        {row.source_filename}
                                      </Typography>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                              Rows {rowsTotal === 0 ? 0 : rowsOffset + 1}-
                              {Math.min(rowsOffset + rows.length, rowsTotal)} of {rowsTotal}
                            </Typography>
                            <Stack direction="row" spacing={1}>
                              <Button
                                size="small"
                                variant="outlined"
                                disabled={rowsOffset === 0 || rowsLoading}
                                onClick={() => expandedId && void loadRows(expandedId, Math.max(0, rowsOffset - ROW_PAGE))}
                              >
                                Previous rows
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                disabled={rowsOffset + ROW_PAGE >= rowsTotal || rowsLoading}
                                onClick={() => expandedId && void loadRows(expandedId, rowsOffset + ROW_PAGE)}
                              >
                                Next rows
                              </Button>
                            </Stack>
                          </Stack>
                        </>
                      )}
                    </Box>
                  )}
                </Paper>
              ))}
              {!listLoading && reports.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No scan reports found.
                </Typography>
              )}
            </Stack>
          )}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default AdminVehicleSoftwareScanPanel;
